import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import DocumentsTab from '@/components/case/DocumentsTab';
import FormsTab from '@/components/case/FormsTab';
import LettersTab from '@/components/case/LettersTab';
import ExhibitListsTab from '@/components/case/ExhibitListsTab';
import DraftingTab from '@/components/case/DraftingTab';
import PetitionTab from '@/components/case/PetitionTab';
import UscisRequestsTab from '@/components/case/UscisRequestsTab';
import { Case, TabType } from '@/types';
import { CasesApi } from '@/api';
import { formatDate, formatRelativeTime } from '@/utils';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';
import {
  RiArrowLeftLine as ArrowLeftIcon,
  RiFileTextLine as DocumentTextIcon,
  RiFileList3Line as FormsIcon,
  RiMailLine as EnvelopeIcon,
  RiEditLine as PencilIcon,
  RiClipboardLine as ClipboardDocumentIcon,
  RiCalendarLine as CalendarIcon,
  RiTimeLine as ClockIcon,
  RiUserLine as UserIcon,
  RiBuildingLine as BuildingIcon,
  RiMoneyDollarCircleLine as CurrencyIcon,
  RiFlag2Line as FlagIcon,
  RiShieldCheckLine as SecurityIcon,
  RiFlowChart as WorkflowIcon,
  RiArrowUpLine,
  RiArrowDownLine,
  RiAlertLine as AlertIcon,
} from 'react-icons/ri';

const StatusBadge: React.FC<{
  status: Case['status'];
  statusRef?: Case['statusRef'];
}> = ({ status, statusRef }) => {
  // Handle both API response formats: status object or statusRef
  const statusObject =
    status && typeof status === 'object' ? status : statusRef;
  const displayStatus =
    statusObject?.name || (typeof status === 'string' ? status : 'unknown');
  const statusColor = statusObject?.color || '#6b7280';

  const statusStyles: { [key: string]: string } = {
    draft: 'bg-neutral-100 text-neutral-800',
    active: 'bg-primary-100 text-primary-800',
    processing: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-success-100 text-success-800',
    archived: 'bg-neutral-100 text-neutral-600',
    new: 'bg-blue-100 text-blue-800',
  };

  const statusKey =
    statusObject?.type?.toLowerCase() ||
    (typeof status === 'string' ? status : 'unknown');
  const defaultStyle =
    statusStyles[statusKey] || 'bg-neutral-100 text-neutral-600';

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${defaultStyle}`}
      style={
        statusObject
          ? { backgroundColor: statusColor + '20', color: statusColor }
          : {}
      }
    >
      {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
    </span>
  );
};

const CasePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [caseInfoExpanded, setCaseInfoExpanded] = useState(false);
  const [isDocumentsProcessing, setIsDocumentsProcessing] = useState(false);
  const [isEditingApplicant, setIsEditingApplicant] = useState(false);
  const [editedApplicantName, setEditedApplicantName] = useState('');

  // Get active tab from URL path
  const getActiveTabFromPath = (): TabType => {
    const path = location.pathname;
    if (path.endsWith('/forms')) return 'forms';
    if (path.endsWith('/letters')) return 'letters';
    if (path.endsWith('/exhibit-lists')) return 'exhibit-lists';
    if (path.endsWith('/drafting')) return 'drafting';
    if (path.endsWith('/petition')) return 'petition';
    if (path.endsWith('/uscis-requests')) return 'uscis-requests';
    if (path.endsWith('/docs')) return 'documents';
    return 'documents'; // default
  };

  const [activeTab, setActiveTab] = useState<TabType>(getActiveTabFromPath());

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getActiveTabFromPath());
  }, [location.pathname]);

  const tabs = [
    {
      id: 'documents',
      name: 'Case Documents',
      icon: DocumentTextIcon,
      soon: false,
    },
    {
      id: 'forms',
      name: 'Forms',
      icon: FormsIcon,
      soon: false,
    },
    {
      id: 'letters',
      name: 'Recommendation Letters',
      icon: EnvelopeIcon,
      soon: false,
    },
    {
      id: 'exhibit-lists',
      name: 'Exhibit Lists',
      icon: ClipboardDocumentIcon,
      soon: false,
    },
    { id: 'drafting', name: 'Drafting', icon: PencilIcon, soon: false },
    {
      id: 'petition',
      name: 'Package',
      icon: ClipboardDocumentIcon,
      soon: false,
    },
    {
      id: 'uscis-requests',
      name: 'USCIS Requests',
      icon: AlertIcon,
      soon: false,
    },
  ];

  // Load case data only when ID changes, not on every path change
  useEffect(() => {
    if (id && typeof id === 'string') {
      loadCase(id);
    }
  }, [id]);

  // Handle redirect to /docs separately, only when needed
  useEffect(() => {
    if (id && location.pathname === `/cases/${id}`) {
      navigate(`/cases/${id}/docs`, { replace: true });
    }
  }, [id, location.pathname, navigate]);

  const loadCase = useCallback(async (caseId: string) => {
    try {
      setIsLoading(true);
      const response = await CasesApi.getCase(caseId);
      console.log('Load case response:', response);
      // Handle the API response format: response.case contains the case data
      const caseInfo = (response as any).case || response.data || response;
      setCaseData(caseInfo);
      setEditedApplicantName(caseInfo.applicantName || '');

      // Track case view
      analytics.track(ANALYTICS_EVENTS.CASE_VIEWED, {
        case_id: caseId,
        visa_type: caseInfo.visaType,
      });
    } catch (err) {
      setError('Failed to load case');
      console.error('Load case error:', err);
      analytics.track(ANALYTICS_EVENTS.ERROR_OCCURRED, {
        error_type: 'case_load_failed',
        error_message: err instanceof Error ? err.message : 'Unknown error',
        case_id: caseId,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleSaveApplicantName = useCallback(async () => {
    if (!id || !editedApplicantName.trim()) return;

    try {
      await CasesApi.updateCase(id, {
        applicantName: editedApplicantName.trim(),
      });
      setCaseData(prev =>
        prev ? { ...prev, applicantName: editedApplicantName.trim() } : null
      );
      setIsEditingApplicant(false);

      // Track applicant name update
      analytics.track(ANALYTICS_EVENTS.CASE_APPLICANT_NAME_UPDATED, {
        case_id: id,
      });
    } catch (err) {
      console.error('Failed to update applicant name:', err);
      alert('Failed to update applicant name');
    }
  }, [id, editedApplicantName]);

  const handleCancelEditApplicant = useCallback(() => {
    setEditedApplicantName(caseData?.applicantName || '');
    setIsEditingApplicant(false);
  }, [caseData?.applicantName]);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (error || !caseData) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-error-600 mb-4">{error || 'Case not found'}</p>
          <Link to="/cases">
            <Button>Back to Cases</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'documents':
        return (
          <DocumentsTab
            caseId={caseData.id}
            visaType={caseData.visaType}
            onLoadingChange={setIsDocumentsProcessing}
          />
        );
      case 'letters':
        return <LettersTab caseId={caseData.id} visaType={caseData.visaType} />;
      case 'forms':
        return <FormsTab caseId={caseData.id} caseData={caseData} />;
      case 'exhibit-lists':
        return <ExhibitListsTab caseId={caseData.id} />;
      case 'petition':
        return (
          <PetitionTab caseId={caseData.id} visaType={caseData.visaType} />
        );
      case 'drafting':
        return <DraftingTab caseId={caseData.id} />;
      case 'uscis-requests':
        return <UscisRequestsTab caseId={caseData.id} />;
      default:
        return (
          <DocumentsTab
            caseId={caseData.id}
            visaType={caseData.visaType}
            onLoadingChange={setIsDocumentsProcessing}
          />
        );
    }
  };

  return (
    <>
      <Helmet>
        <title>{caseData.name || 'Case'} - ImmStack</title>
      </Helmet>
      <Layout>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/cases">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center space-x-1 flex-shrink-0"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Back to Cases</span>
                  <span className="sm:hidden">Back</span>
                </Button>
              </Link>
              <h1 className="text-xl font-semibold text-neutral-900 flex items-center gap-2">
                {caseData.name}
                {isDocumentsProcessing && (
                  <span className="inline-flex items-center gap-1.5 text-sm text-blue-600">
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="font-medium">Loading</span>
                  </span>
                )}
              </h1>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const newState = !caseInfoExpanded;
                setCaseInfoExpanded(newState);

                // Track case info toggle
                if (id) {
                  analytics.track(ANALYTICS_EVENTS.CASE_INFO_TOGGLED, {
                    case_id: id,
                    expanded: newState,
                  });
                }
              }}
              className="flex items-center space-x-2"
            >
              <span>{caseInfoExpanded ? 'Hide' : 'Show'} Case Info</span>
              {caseInfoExpanded ? (
                <RiArrowUpLine className="h-4 w-4" />
              ) : (
                <RiArrowDownLine className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Case Info */}
          {caseInfoExpanded && (
            <Card padding="md">
              {/* Detailed Info Grid */}
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                {/* Applicant Info */}
                {caseData.applicantName && (
                  <div>
                    <div className="flex items-center space-x-1 text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                      <UserIcon className="h-3 w-3" />
                      <span>Applicant</span>
                    </div>
                    {isEditingApplicant ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={editedApplicantName}
                          onChange={e => setEditedApplicantName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleSaveApplicantName();
                            if (e.key === 'Escape') handleCancelEditApplicant();
                          }}
                          className="text-sm font-medium text-neutral-900 px-2 py-1 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 flex-1 min-w-0"
                          autoFocus
                        />
                        <button
                          onClick={handleSaveApplicantName}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                          title="Save"
                          aria-label="Save applicant name"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={handleCancelEditApplicant}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Cancel"
                          aria-label="Cancel editing applicant name"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 group">
                        <p className="text-sm font-medium text-neutral-900 truncate">
                          {caseData.applicantName}
                        </p>
                        <button
                          onClick={() => setIsEditingApplicant(true)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-neutral-400 hover:text-primary-600 rounded transition-opacity"
                          title="Edit applicant name"
                          aria-label="Edit applicant name"
                        >
                          <PencilIcon className="h-3 w-3" aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Visa Type */}
                {caseData.visaType && (
                  <div>
                    <div className="flex items-center space-x-1 text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                      <DocumentTextIcon className="h-3 w-3" />
                      <span>Visa Type</span>
                    </div>
                    <p className="text-sm font-medium text-neutral-900">
                      {caseData.visaType}
                    </p>
                  </div>
                )}

                {/* Petitioner Company Name - only for O-1A */}
                {caseData.petitionerCompanyName &&
                  caseData.visaType === 'O-1A' && (
                    <div>
                      <div className="flex items-center space-x-1 text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                        <BuildingIcon className="h-3 w-3" />
                        <span>Petitioner Company</span>
                      </div>
                      <p className="text-sm font-medium text-neutral-900 truncate">
                        {caseData.petitionerCompanyName}
                      </p>
                    </div>
                  )}

                {/* Created By */}
                {caseData.creator && (
                  <div>
                    <div className="flex items-center space-x-1 text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                      <UserIcon className="h-3 w-3" />
                      <span>Created By</span>
                    </div>
                    <p className="text-sm font-medium text-neutral-900 truncate">
                      {caseData.creator.name || caseData.creator.email}
                    </p>
                  </div>
                )}

                {/* Last Updated */}
                <div>
                  <div className="flex items-center space-x-1 text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                    <ClockIcon className="h-3 w-3" />
                    <span>Updated</span>
                  </div>
                  <p
                    className="text-sm font-medium text-neutral-900"
                    title={formatDate(caseData.updatedAt)}
                  >
                    {formatRelativeTime(caseData.updatedAt)}
                  </p>
                </div>

                {/* Filing Date */}
                {caseData.filingDate && (
                  <div>
                    <div className="flex items-center space-x-1 text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                      <CalendarIcon className="h-3 w-3" />
                      <span>Filed</span>
                    </div>
                    <p className="text-sm font-medium text-neutral-900">
                      {formatDate(caseData.filingDate)}
                    </p>
                  </div>
                )}

                {/* Due Date */}
                {caseData.dueDate && (
                  <div>
                    <div className="flex items-center space-x-1 text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                      <CalendarIcon className="h-3 w-3" />
                      <span>Due Date</span>
                    </div>
                    <p className="text-sm font-medium text-neutral-900">
                      {formatDate(caseData.dueDate)}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Tabs */}
          <div className="border-b border-neutral-200 overflow-x-auto">
            <nav className="flex space-x-4 md:space-x-8 min-w-max">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const disabled = tab.soon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      if (disabled) return;
                      const previousTab = activeTab;
                      const tabPath = tab.id === 'documents' ? 'docs' : tab.id;

                      // Track tab change
                      if (previousTab !== tab.id && id) {
                        analytics.track(ANALYTICS_EVENTS.TAB_CHANGED, {
                          case_id: id,
                          from_tab: previousTab,
                          to_tab: tab.id,
                          visa_type: caseData?.visaType,
                        });
                      }

                      navigate(`/cases/${id}/${tabPath}`);
                    }}
                    aria-disabled={disabled}
                    aria-current={isActive ? 'page' : undefined}
                    aria-label={`${tab.name} tab${disabled ? ' - Coming soon' : ''}`}
                    className={`group flex items-center space-x-2 py-2 px-2 md:px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap rounded-t focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${
                      isActive
                        ? 'border-primary-500 text-primary-600'
                        : disabled
                          ? 'border-transparent text-neutral-400 cursor-not-allowed'
                          : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                    }`}
                    title={disabled ? 'Coming soon' : tab.name}
                  >
                    <Icon
                      className={`h-4 w-4 flex-shrink-0 ${disabled ? 'opacity-50' : ''}`}
                    />
                    <span className="flex items-center gap-1">
                      {tab.name}
                      {tab.soon && (
                        <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500 group-hover:bg-neutral-200">
                          Soon
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="w-full">{renderTabContent()}</div>
        </div>
      </Layout>
    </>
  );
};

export default CasePage;
